import React, { useMemo } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import type { Sponsor, SponsorTelemed } from '../services/sponsorService';

export interface ProfessionalDetailLabels {
  crp: string;
  session: string;
  price: string;
}

export interface ProfessionalDetailRow {
  key: 'crp' | 'session' | 'price';
  label: string;
  value: string;
}

interface ProfessionalDetailsProps {
  sponsor: Pick<Sponsor, 'crp' | 'telemed'>;
  labels: ProfessionalDetailLabels;
  locale: string;
  formatSessionDuration: (minutes: number) => string;
  withTopDivider?: boolean;
  visibleRows?: readonly ProfessionalDetailRow['key'][];
}

function normalizedCrp(crp: Sponsor['crp']): string | null {
  if (typeof crp !== 'string') return null;
  const value = crp.trim();
  return value.length > 0 ? value : null;
}

export function formatMinorCurrency(
  amountMinor: SponsorTelemed['amount_minor'],
  currency: SponsorTelemed['currency'],
  locale: string
): string | null {
  if (
    !Number.isInteger(amountMinor) ||
    typeof amountMinor !== 'number' ||
    amountMinor < 0 ||
    typeof currency !== 'string' ||
    !/^[A-Za-z]{3}$/.test(currency)
  ) {
    return null;
  }

  try {
    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency.toUpperCase(),
    });
    const fractionDigits = formatter.resolvedOptions().maximumFractionDigits ?? 2;
    return formatter.format(amountMinor / 10 ** fractionDigits);
  } catch {
    return null;
  }
}

export function buildProfessionalDetailRows(
  sponsor: Pick<Sponsor, 'crp' | 'telemed'>,
  labels: ProfessionalDetailLabels,
  locale: string,
  formatSessionDuration: (minutes: number) => string
): ProfessionalDetailRow[] {
  const rows: ProfessionalDetailRow[] = [];
  const crp = normalizedCrp(sponsor.crp);

  if (crp) {
    rows.push({ key: 'crp', label: labels.crp, value: crp });
  }

  if (sponsor.telemed?.enabled === true) {
    const durationMinutes = sponsor.telemed.duration_minutes;
    if (
      typeof durationMinutes === 'number' &&
      Number.isInteger(durationMinutes) &&
      durationMinutes > 0
    ) {
      rows.push({
        key: 'session',
        label: labels.session,
        value: formatSessionDuration(durationMinutes),
      });
    }

    const price = formatMinorCurrency(
      sponsor.telemed.amount_minor,
      sponsor.telemed.currency,
      locale
    );
    if (price) {
      rows.push({ key: 'price', label: labels.price, value: price });
    }
  }

  return rows;
}

export default function ProfessionalDetails({
  sponsor,
  labels,
  locale,
  formatSessionDuration,
  withTopDivider = false,
  visibleRows,
}: ProfessionalDetailsProps) {
  const rows = useMemo(() => {
    const allRows = buildProfessionalDetailRows(sponsor, labels, locale, formatSessionDuration);
    return visibleRows ? allRows.filter((row) => visibleRows.includes(row.key)) : allRows;
  }, [formatSessionDuration, labels, locale, sponsor, visibleRows]);

  if (rows.length === 0) return null;

  return (
    <View style={[styles.container, withTopDivider && styles.containerWithTopDivider]}>
      {rows.map((row) => (
        <View key={row.key} style={styles.row}>
          <Text style={styles.label}>{row.label}</Text>
          <Text style={styles.value}>{row.value}</Text>
        </View>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    gap: 16,
  },
  containerWithTopDivider: {
    borderTopWidth: 1,
    borderTopColor: '#F0EEE4',
    paddingTop: 16,
  },
  row: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
  },
  label: {
    color: '#807E79',
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    lineHeight: 14,
  },
  value: {
    flexShrink: 1,
    color: '#151515',
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    lineHeight: 19.6,
    textAlign: 'right',
  },
});
